import { Location } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
  AuthManagementApiService,
  GetToken,
  LoginCommand,
  TwoFactorAuthLoginCommand,
  TwoFactorAuthRecoveryCommand,
} from '@api-open';

import { DecodedToken, decodeToken } from '../lib';

const TOKEN = 'token';
const REFRESH_TOKEN = 'refresh_token';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  logoutEvent$: Observable<void>;

  private decodedToken: DecodedToken | null = null;

  private loginRedirectUrl?: string;

  private logout$ = new Subject<void>();

  constructor(
    private authManagementApiService: AuthManagementApiService,
    private dialog: MatDialog,
    private router: Router,
    private location: Location,
  ) {
    this.logoutEvent$ = this.logout$.asObservable();

    const token = localStorage.getItem(TOKEN);
    if (token) {
      this.decodeToken(token);
    }
  }

  login(cmd: LoginCommand): Observable<GetToken> {
    return this.authManagementApiService.authLogin({ loginCommand: cmd }).pipe(
      tap((token) => {
        this.storeTokens(token);
      }),
    );
  }

  loginWith2fa(cmd: TwoFactorAuthLoginCommand, context?: HttpContext): Observable<GetToken> {
    return this.authManagementApiService
      .twoFactorAuthLogin({ twoFactorAuthLoginCommand: cmd }, 'body', false, {
        context,
      })
      .pipe(tap((token) => this.storeTokens(token)));
  }

  loginWithRecoveryCode(cmd: TwoFactorAuthRecoveryCommand, context?: HttpContext): Observable<GetToken> {
    return this.authManagementApiService
      .twoFactorAuthRecovery({ twoFactorAuthRecoveryCommand: cmd }, 'body', false, {
        context,
      })
      .pipe(tap((token) => this.storeTokens(token)));
  }

  refreshToken(): Observable<GetToken> {
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();
    if (!token || !refreshToken) {
      this.logout$.next();
      const result = new ReplaySubject<GetToken>(1);
      result.error(new Error('Session expired.'));
      return result;
    }

    const refreshTokenCommand = { token, refreshToken };
    return this.authManagementApiService.authRefresh({ refreshTokenCommand }).pipe(
      tap((newToken) => {
        if (newToken) {
          this.storeTokens(newToken);
        }
      }),
    );
  }

  getDecodedToken(): DecodedToken | null {
    return this.decodedToken;
  }

  getToken(): string {
    return localStorage.getItem(TOKEN) ?? '';
  }

  loggedIn(): boolean {
    return (
      !!this.decodedToken && this.decodedToken['2fa_pending'] !== 'True' && this.decodedToken['2fa_pending'] !== 'true'
    );
  }

  logout({ saveLocationForRedirect = false } = {}): void {
    this.logout$.next();
    this.removeTokens();
    this.dialog.closeAll();

    if (saveLocationForRedirect) {
      this.setLoginRedirectUrl(this.location.path());
    }

    void this.router.navigate(['/login']);
  }

  setLoginRedirectUrl(url: string | undefined): void {
    this.loginRedirectUrl = url;
  }

  async navigateAfterLogin(token?: GetToken): Promise<void> {
    if (token) this.storeTokens(token);

    let redirectTo = this.loginRedirectUrl;
    if (!redirectTo) {
      redirectTo = '/home';
    }
    await this.router.navigateByUrl(redirectTo);
    this.setLoginRedirectUrl(undefined);
  }

  private storeTokens(token: GetToken): void {
    localStorage.setItem(TOKEN, token.token);
    this.decodeToken(token.token);

    if (token.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN, token.refreshToken);
    }
  }

  private decodeToken(token: string): void {
    this.decodedToken = decodeToken(token);
  }

  private removeTokens(): void {
    this.decodedToken = null;
    localStorage.removeItem(TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
  }

  private getRefreshToken(): string {
    return localStorage.getItem(REFRESH_TOKEN) ?? '';
  }
}
